/**
 * AI Cost Trends - Main Lab Component
 *
 * Interactive visualization showing how AI API pricing has evolved over time.
 * Tracks multiple providers (OpenAI, Anthropic, Google, Mistral) with
 * verifiable source links for each data point.
 */

import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, TrendingDown, Building2, Download } from 'lucide-react'
import {
  useCostData,
  useModelSummaries,
  useCostStats,
} from './useCostData'
import { CostTrendsChart } from './CostTrendsChart'
import { ProviderSegments } from './ProviderSegments'
import { SourceTable } from './SourceTable'
import { StatsCard } from './StatsCard'
import type { CostTrendsFilterState } from './types'

// LocalStorage keys
const STORAGE_KEY_FILTERS = 'ai-cost-trends-filters'
const STORAGE_KEY_TABLE = 'ai-cost-trends-table'

// Format date as "Mon YYYY" (e.g., "Feb 2023")
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

// Default filter values
const defaultFilters: CostTrendsFilterState = {
  selectedProviders: ['openai'],
  priceType: 'input',
  compareMode: false,
  compareModels: null,
  dateRange: ['2023-01-01', '2025-12-31'],
}

// Load state from localStorage with fallback
function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const stored = localStorage.getItem(key)
    if (stored) {
      return JSON.parse(stored) as T
    }
  } catch {
    // Ignore parse errors, use fallback
  }
  return fallback
}

// Save state to localStorage
function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
  }
}

export const AICostTrends: React.FC = () => {
  const { t } = useTranslation('aiCostTrends')
  const { data, loading, error } = useCostData()

  // Filter state - load from localStorage or use defaults
  const [filters, setFilters] = useState<CostTrendsFilterState>(() =>
    loadFromStorage(STORAGE_KEY_FILTERS, defaultFilters)
  )

  // Table filter state - load from localStorage
  const [tableProviderFilter, setTableProviderFilter] = useState<string | null>(() =>
    loadFromStorage<{ provider: string | null }>(STORAGE_KEY_TABLE, { provider: null }).provider
  )
  const [tableSearch, setTableSearch] = useState('')

  // Persist chart filters to localStorage
  useEffect(() => {
    saveToStorage(STORAGE_KEY_FILTERS, filters)
  }, [filters])

  // Persist table filters to localStorage
  useEffect(() => {
    saveToStorage(STORAGE_KEY_TABLE, { provider: tableProviderFilter })
  }, [tableProviderFilter])

  const [highlightedModel, setHighlightedModel] = useState<string | null>(null)

  // Compute data - stats are scoped to selected providers
  const summaries = useModelSummaries(data, filters)
  const stats = useCostStats(data, filters.selectedProviders)

  // Filter summaries by selected providers (for chart)
  const filteredSummaries = useMemo(() => {
    if (filters.selectedProviders.length === 0) return summaries
    return summaries.filter((s) =>
      filters.selectedProviders.includes(s.providerId)
    )
  }, [summaries, filters.selectedProviders])

  // All summaries for table (with its own filtering)
  const allSummaries = useModelSummaries(data, { ...filters, selectedProviders: [] })

  // Filter summaries for table (separate from chart)
  const tableSummaries = useMemo(() => {
    let result = allSummaries
    if (tableProviderFilter) {
      result = result.filter((s) => s.providerId === tableProviderFilter)
    }
    if (tableSearch) {
      const search = tableSearch.toLowerCase()
      result = result.filter(
        (s) =>
          s.modelName.toLowerCase().includes(search) ||
          s.providerName.toLowerCase().includes(search)
      )
    }
    return result
  }, [allSummaries, tableProviderFilter, tableSearch])

  // Provider selection handlers
  // Single select: shows only that provider
  const handleProviderSelect = (providerId: string) => {
    setFilters((f) => ({
      ...f,
      selectedProviders: [providerId],
      compareMode: false,
    }))
  }

  // Compare toggle: add/remove provider from comparison
  const handleProviderToggleCompare = (providerId: string) => {
    setFilters((f) => {
      const current = f.selectedProviders
      if (current.includes(providerId)) {
        // Remove provider (but keep at least one)
        const newSelection = current.filter((id) => id !== providerId)
        return {
          ...f,
          selectedProviders: newSelection.length > 0 ? newSelection : [],
        }
      } else {
        // Add provider
        return {
          ...f,
          selectedProviders: [...current, providerId],
        }
      }
    })
  }

  const handleSelectAll = () => {
    setFilters((f) => ({ ...f, selectedProviders: [], compareMode: false }))
  }

  const handleCompareModeChange = (enabled: boolean) => {
    if (enabled) {
      // Entering compare mode - keep current selection or start fresh
      setFilters((f) => ({ ...f, compareMode: true }))
    } else {
      // Exiting compare mode - reset to show all providers
      setFilters((f) => ({ ...f, compareMode: false, selectedProviders: [] }))
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-accent animate-spin mx-auto" />
          <p className="text-secondary">{t('loading')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-red-500">{t('errorTitle')}</p>
          <p className="text-secondary text-sm">{error}</p>
        </div>
      </div>
    )
  }

  // Build cost reduction sublabel
  const costReductionSublabel = stats.mostExpensiveModel && stats.cheapestModel
    ? t('stats.costReductionSublabel', {
        from: stats.mostExpensiveModel.name,
        fromPrice: stats.mostExpensiveModel.price,
        fromDate: formatDate(stats.mostExpensiveModel.date),
        to: stats.cheapestModel.name,
        toPrice: stats.cheapestModel.price.toFixed(2),
        toDate: formatDate(stats.cheapestModel.date),
      })
    : t('stats.costReductionDefault')

  return (
    <div className="space-y-10">
      {/* Hero stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          value={stats.overallReduction > 0 ? `${stats.overallReduction.toFixed(0)}x` : '-'}
          label={t('stats.costReduction')}
          sublabel={costReductionSublabel}
          color="#10B981"
        />
        <StatsCard
          value={
            stats.cheapestModel
              ? `$${stats.cheapestModel.price.toFixed(2)}`
              : '-'
          }
          label={t('stats.cheapestInput')}
          sublabel={
            stats.cheapestModel
              ? `${stats.cheapestModel.name} (${formatDate(stats.cheapestModel.date)})`
              : ''
          }
        />
        <StatsCard
          value={stats.totalModels.toString()}
          label={t('stats.modelsTracked')}
          sublabel={t('stats.modelsTrackedSublabel', { count: stats.totalProviders })}
        />
        <StatsCard
          value={
            stats.largestReduction
              ? `${stats.largestReduction.reduction.toFixed(0)}%`
              : '-'
          }
          label={t('stats.largestDrop')}
          sublabel={stats.largestReduction?.model || ''}
          color="#3B82F6"
        />
      </section>

      {/* Main chart section */}
      <section className="bg-secondary rounded-lg p-5">
        {/* Controls row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <ProviderSegments
            providers={data.providers}
            selectedProviders={filters.selectedProviders}
            compareMode={filters.compareMode}
            onSelect={handleProviderSelect}
            onToggleCompare={handleProviderToggleCompare}
            onSelectAll={handleSelectAll}
            onCompareModeChange={handleCompareModeChange}
          />

          <div className="flex gap-2 ml-auto">
            <button
              onClick={() =>
                setFilters((f) => ({ ...f, priceType: 'input' }))
              }
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filters.priceType === 'input'
                  ? 'bg-accent text-white'
                  : 'bg-tertiary text-secondary hover:text-primary'
              }`}
            >
              {t('chart.input')}
            </button>
            <button
              onClick={() =>
                setFilters((f) => ({ ...f, priceType: 'output' }))
              }
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filters.priceType === 'output'
                  ? 'bg-accent text-white'
                  : 'bg-tertiary text-secondary hover:text-primary'
              }`}
            >
              {t('chart.output')}
            </button>
          </div>
        </div>

        {/* Chart */}
        <CostTrendsChart
          summaries={filteredSummaries}
          priceType={filters.priceType}
          highlightedModel={highlightedModel}
          onModelHover={setHighlightedModel}
        />

        <p className="text-xs text-secondary mt-4 text-center">
          {t('chart.helpText')}
        </p>
      </section>

      {/* Insights section - condensed */}
      <section className="bg-secondary rounded-lg p-5">
        <h2 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-green-500" />
          {t('insights.title')}
        </h2>
        <div className="prose prose-sm max-w-none text-secondary">
          <p className="mb-4">
            {t('insights.description', {
              reduction: stats.overallReduction > 1
                ? t('insights.reductionNote', { value: Math.round(stats.overallReduction) })
                : '',
            })}
            <strong className="text-primary"> {t('insights.factor1')}</strong> {t('insights.factor1Detail')},
            <strong className="text-primary"> {t('insights.factor2')}</strong> {t('insights.factor2Detail')}, and
            <strong className="text-primary"> {t('insights.factor3')}</strong> {t('insights.factor3Detail')}.
          </p>
          <p className="mb-0">
            {t('insights.competition')}
          </p>
        </div>
      </section>

      {/* Source table */}
      <section>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t('table.title')}
          </h2>
          <div className="flex items-center gap-3">
            {/* Search */}
            <input
              type="text"
              placeholder={t('table.searchPlaceholder')}
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              className="px-3 py-1.5 text-sm bg-tertiary rounded-md border border-secondary focus:outline-none focus:border-accent w-40"
            />
            {/* Provider filter */}
            <select
              value={tableProviderFilter || ''}
              onChange={(e) => setTableProviderFilter(e.target.value || null)}
              className="px-3 py-1.5 text-sm bg-tertiary rounded-md border border-secondary focus:outline-none focus:border-accent"
            >
              <option value="">{t('table.allProviders')}</option>
              {data.providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            {/* Download */}
            <a
              href="/data/ai-cost-trends.json"
              download="ai-cost-trends.json"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-tertiary rounded-md hover:bg-accent/20 transition-colors"
              title="Download JSON data"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('table.json')}</span>
            </a>
          </div>
        </div>
        <div className="bg-secondary rounded-lg p-5">
          <SourceTable summaries={tableSummaries} priceType={filters.priceType} />
        </div>
      </section>

      {/* Footer */}
      <section className="text-center py-8 border-t border-secondary">
        <p className="text-sm text-secondary">
          {t('footer.dataSource', { date: new Date(data.lastUpdated).toLocaleDateString() })}
        </p>
      </section>
    </div>
  )
}

export default AICostTrends
